import axios from 'axios'
import axiosJsonpAdapter from './axios-jsonp'
import { extend, strictExtend, getBaseByUrl, getParamsByUrl } from '@shared/utils/base/index.js'

const isFunction = (functionToCheck) => {
  return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]'
}

const isObject = (val) => {
  return typeof val === 'object'
}

class NetworkError extends Error {
  constructor (message) {
    super(message)
    this.message = message // message单元测试用到
  }
}

class DataService {
  constructor (opt = {}) {
    this.interfaceMap = {}
    this.cancelTokenSourceMap = {}
    this.baseConfig = strictExtend({
      // requestDataFn: function() {},
      // responseSuccessFn: function () {},
      // responseFailFn: function () {},
      retry: {
        times: 0,
        delay: 200
        // _retryTime // 内部使用，用于尝试次数的计数
      }
    }, opt.default)

    this.axiosInstance = axios.create()
  }

  /**
   *
   * @param {string} groupName 组名
   * @param {string|object} opt 默认选项，传string代表为baseUrl
   * @param {object} intfcListMap 接口配置
   */
  add (groupName, opt, intfcListMap) {
    if (!(groupName && intfcListMap)) return

    let defaultOpt = {}

    if (isObject(opt)) {
      defaultOpt = opt
    } else if (typeof opt === 'string') {
      defaultOpt.baseUrl = opt
    }

    if (!this.interfaceMap[groupName]) {
      this.interfaceMap[groupName] = {}
    }

    Object.keys(intfcListMap).forEach((key) => {
      const intfcInfo = intfcListMap[key]
      this.interfaceMap[groupName][key] = (opt) => {
        const intfcInfoUrl = intfcInfo.url
        let options = extend(true, {}, this.baseConfig, defaultOpt, intfcInfo, {
          url: getBaseByUrl(intfcInfoUrl),
          data: getParamsByUrl(intfcInfoUrl)
        }, opt)
        options.url = `${options.baseUrl || ''}${options.url}`
        if (/get/i.test(options.method)) {
          options = extend(options, {
            params: options.data
          })
        }
        if (options.debugOpt) { // 用于单元测试
          return this.getAxiosOpt(options)
        }
        return this._getData(options)
      }
    })
  }

  get (groupName) {
    return this.interfaceMap[groupName]
  }

  getAxiosOpt (intfcInfo) {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    let opt = extend(true, {
      cancelToken: source.token
    }, intfcInfo)

    const reqId = opt.reqId
    if (reqId) { // 处理请求未完成，再次发送请求的问题
      const _source = this.cancelTokenSourceMap[reqId]
      if (_source) {
        _source.cancel('Operation canceled')
      }
      this.cancelTokenSourceMap[reqId] = source
    }

    if (opt.dataType === 'jsonp') {
      opt.adapter = axiosJsonpAdapter
    }

    if (isFunction(intfcInfo.requestDataFn)) {
      opt = intfcInfo.requestDataFn(opt)
      if (/get/i.test(opt.method)) {
        opt.params = opt.data
      }
    }
    return opt
  }

  _handleException (options, err) {
    const config = options.__cache || options

    // 设置重试次数
    config.retry._retryTime = config.retry._retryTime || 0

    if (config.retry._retryTime >= config.retry.times) {
      if (isFunction(config.responseFailFn)) {
        return config.responseFailFn(err)
      }

      if (config.debugRetry) {
        // 错误回调
        return Promise.reject(new NetworkError({ err, config }))
      }
      // 错误回调
      return Promise.reject(new NetworkError(err))
    }

    config.retry._retryTime += 1

    // 创建重试延迟
    const delay = new Promise((resolve) => {
      setTimeout(() => {
        resolve()
      }, config.retry.delay)
    })

    // 创建重试句柄
    return delay.then(() => {
      return this._getData(config, false)
    })
  }

  _getData (options, isFormat) {
    return this.__getData(options, isFormat).then((res) => {
      if (isFunction(options.responseSuccessFn)) {
        return options.responseSuccessFn(res)
      }
      return res
    }).catch(err => {
      return this._handleException(options, err)
    })
  }

  // 最底层调用axios发送的请求
  __getData (intfcInfo, isFormat = true) {
    let opt
    if (isFormat) {
      opt = this.getAxiosOpt(intfcInfo)
      intfcInfo.__cache = opt
    } else {
      opt = intfcInfo
    }

    if (opt.debugSuccess) {
      return Promise.resolve({ ret: 0 })
    } else if (opt.debugFail) {
      return Promise.reject(new Error('request fail'))
    }
    return this.axiosInstance(opt)
  }
}

export default DataService
